import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Component,
  Input,
  OnInit,
  DestroyRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormArray,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';

import { ComboboxOption, SuiComboboxComponent } from '@sui/combobox';
import { Store } from '@ngrx/store';

import { UserApiService } from 'src/app/api/services/user-api.service';
import { UtilsDataObjectApiService } from 'src/app/api/services/utils-data-object-api.service';

import { DataObject, NomItem } from 'src/app/core/models/core.models';
import { NomObjectLifecycleStatus } from 'src/app/core/constants';

import { AppState } from 'src/app/core/state/app.state';
import { selectCurrentCompanyId } from 'src/app/core/state';

import { Observable, BehaviorSubject, forkJoin, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { NgpLabel } from 'ng-primitives/form-field';
import { SuiLabelDirective } from '@sui/form-field';
import { SuiCheckboxComponent } from '@sui/checkbox';
import { AsyncPipe } from '@angular/common';

interface GroupedObjects {
  feeds: DataObject[];
  dashboards: DataObject[];
  dimensions: DataObject[];
}

@Component({
  selector: 'app-workspace-duplicate-form',
  templateUrl: './workspace-duplicate-form.component.html',
  styleUrl: './workspace-duplicate-form.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgpLabel,
    SuiLabelDirective,
    SuiComboboxComponent,
    SuiCheckboxComponent,
    AsyncPipe,
  ],
})
export class WorkspaceDuplicateFormComponent implements OnInit {
  @Input() objects: DataObject[];

  public form: FormGroup;
  public companies: NomItem[] = [];
  public users$: Observable<any[]>;
  // sui-combobox option lists ({ name, id } → { label, value }).
  public companyOptions: ComboboxOption[] = [];
  public userOptions$: Observable<ComboboxOption[]>;
  private currentCompanyId: string;
  public groupedObjects: GroupedObjects = {
    feeds: [],
    dashboards: [],
    dimensions: [],
  };
  // * "selectAll" -> maps to inverted "copy_only_selected"
  public selectAll = true;
  public showObjectsSection = false;

  // ! integration with parent popup component
  public formGroupChange: Observable<FormGroup>;
  public componentDataChange: Observable<any>;
  private formGroupSubject = new BehaviorSubject<FormGroup>(null);
  private componentDataSubject = new BehaviorSubject<any>(null);

  private destroyRef = inject(DestroyRef);

  constructor(
    private fb: FormBuilder,
    private utilsDataObjectApi: UtilsDataObjectApiService,
    private userApi: UserApiService,
    private store: Store<AppState>,
  ) {
    // ! initialize observables for parent popup integration
    this.formGroupChange = this.formGroupSubject.asObservable();
    this.componentDataChange = this.componentDataSubject.asObservable();
  }

  ngOnInit(): void {
    this.store
      .select(selectCurrentCompanyId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((companyId) => {
        this.currentCompanyId = companyId;
      });

    this.initializeForm();
    this.loadCompanies();
    this.fetchWorkspaceContents();
    this.setupFormSubscriptions();

    // ! emit form group to parent popup for integration
    this.formGroupSubject.next(this.form);
  }

  initializeForm(): void {
    this.form = this.fb.group({
      company_id: [null, [Validators.required]],
      target_user_id: [null, [Validators.required]],
      copy_only_selected: [false],
      feed_ids: this.fb.array([]),
      dashboard_ids: this.fb.array([]),
      dimension_ids: this.fb.array([]),
    });
  }

  private loadCompanies(): void {
    this.utilsDataObjectApi
      .getCompanies()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((companies) => {
          // ! filter out current Company
          // ! must prevent selecting source as target
          this.companies = companies.filter(
            (company) => company.id !== this.currentCompanyId,
          );
          this.companyOptions = this.companies.map((company) => ({
            label: company.name,
            value: company.id,
          }));
        }),
      )
      .subscribe();
  }

  private setupFormSubscriptions(): void {
    // * "company_id" selection changes
    this.form
      .get('company_id')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((companyId: string) => {
        this.onCompanyChange(companyId);
      });

    // * "target_user_id" selection changes
    this.form
      .get('target_user_id')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((userId: string) => {
        this.onUserChange(userId);
      });

    // * Form changes for Parent Popup communication
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.emitDataChange('form_updated', {
          isValid: this.isFormValid(),
          formValue: this.form.value,
        });
      });
  }

  private onCompanyChange(companyId: string): void {
    if (companyId) {
      this.form.patchValue(
        {
          target_user_id: null,
        },
        { emitEvent: false },
      );

      this.showObjectsSection = false;

      this.resetObjectSelections();

      this.users$ = this.userApi.listCompanyUsers(companyId);
      this.userOptions$ = this.users$.pipe(
        map((users) =>
          (users ?? []).map((user) => ({
            label: user.name,
            value: user.id,
          })),
        ),
      );
    }
  }

  private onUserChange(userId: string): void {
    if (userId) {
      this.showObjectsSection = true;
    } else {
      this.showObjectsSection = false;
    }
  }

  private fetchWorkspaceContents(): void {
    const workspaceRequests = this.objects.map((workspace) =>
      // * a plain api-service call rather than the workspace-objects query: this
      // * dialog needs several workspaces' contents, fresh, in one pass
      this.userApi.getUserWorkspaceObjects(workspace.id, [
        NomObjectLifecycleStatus.ACTIVE,
      ]),
    );

    forkJoin(workspaceRequests)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((responses) => {
          // ! combine all Objects from all Workspaces
          const allFeeds = responses.flatMap(
            (response) => response.feeds || [],
          );
          const allDashboards = responses.flatMap(
            (response) => response.dashboards || [],
          );
          const allDimensions = responses.flatMap(
            (response) => response.dimensions || [],
          );

          this.groupedObjects = {
            feeds: allFeeds,
            dashboards: allDashboards,
            dimensions: allDimensions,
          };

          this.initializeObjectSelections();
        }),
        catchError((error) => {
          this.showObjectsSection = false;

          return throwError(() => error);
        }),
      )
      .subscribe();
  }

  private resetObjectSelections(): void {
    this.selectAll = true;
    this.form.patchValue({ copy_only_selected: false }, { emitEvent: false });
    this.clearFormArrays();

    if (
      this.groupedObjects.feeds.length > 0 ||
      this.groupedObjects.dashboards.length > 0 ||
      this.groupedObjects.dimensions.length > 0
    ) {
      this.initializeObjectSelections();
    }
  }

  private initializeObjectSelections(): void {
    this.selectAll = true;
    this.form.patchValue({ copy_only_selected: false }); // * inverted logic

    this.clearFormArrays();

    this.groupedObjects.feeds.forEach((obj) =>
      this.addToFormArray('feed_ids', obj.id),
    );
    this.groupedObjects.dashboards.forEach((obj) =>
      this.addToFormArray('dashboard_ids', obj.id),
    );
    this.groupedObjects.dimensions.forEach((obj) =>
      this.addToFormArray('dimension_ids', obj.id),
    );
  }

  public onSelectAllChange(): void {
    this.selectAll = !this.selectAll;
    this.form.patchValue({ copy_only_selected: !this.selectAll }); // * inverted logic

    if (this.selectAll) {
      this.clearFormArrays();
      this.groupedObjects.feeds.forEach((obj) =>
        this.addToFormArray('feed_ids', obj.id),
      );
      this.groupedObjects.dashboards.forEach((obj) =>
        this.addToFormArray('dashboard_ids', obj.id),
      );
      this.groupedObjects.dimensions.forEach((obj) =>
        this.addToFormArray('dimension_ids', obj.id),
      );
    } else {
      this.clearFormArrays();
    }
  }

  public onObjectToggle(
    objectId: string,
    objectType: 'feeds' | 'dashboards' | 'dimensions',
  ): void {
    const arrayName =
      objectType === 'feeds'
        ? 'feed_ids'
        : objectType === 'dashboards'
          ? 'dashboard_ids'
          : 'dimension_ids';

    const formArray = this.form.get(arrayName) as FormArray;
    const index = formArray.controls.findIndex(
      (control) => control.value === objectId,
    );

    if (index >= 0) {
      // * remove from selection
      formArray.removeAt(index);
    } else {
      // * add to selection
      this.addToFormArray(arrayName, objectId);
    }

    this.updateSelectAllState();
  }

  public isObjectSelected(
    objectId: string,
    objectType: 'feeds' | 'dashboards' | 'dimensions',
  ): boolean {
    const arrayName =
      objectType === 'feeds'
        ? 'feed_ids'
        : objectType === 'dashboards'
          ? 'dashboard_ids'
          : 'dimension_ids';

    const formArray = this.form.get(arrayName) as FormArray;
    return formArray.controls.some((control) => control.value === objectId);
  }

  private updateSelectAllState(): void {
    const totalObjects =
      this.groupedObjects.feeds.length +
      this.groupedObjects.dashboards.length +
      this.groupedObjects.dimensions.length;

    const selectedObjects =
      (this.form.get('feed_ids') as FormArray).length +
      (this.form.get('dashboard_ids') as FormArray).length +
      (this.form.get('dimension_ids') as FormArray).length;

    this.selectAll = selectedObjects === totalObjects;
    this.form.patchValue(
      { copy_only_selected: !this.selectAll },
      { emitEvent: false },
    );
  }

  private clearFormArrays(): void {
    (this.form.get('feed_ids') as FormArray).clear();
    (this.form.get('dashboard_ids') as FormArray).clear();
    (this.form.get('dimension_ids') as FormArray).clear();
  }

  private addToFormArray(arrayName: string, value: string): void {
    const formArray = this.form.get(arrayName) as FormArray;
    formArray.push(this.fb.control(value));
  }

  public isFormValid(): boolean {
    const targetCompanyId = this.form.get('company_id')?.value;
    const hasCompany = !!targetCompanyId;
    const hasUser = !!this.form.get('target_user_id')?.value;
    const hasSelectedObjects =
      (this.form.get('feed_ids') as FormArray).length > 0 ||
      (this.form.get('dashboard_ids') as FormArray).length > 0 ||
      (this.form.get('dimension_ids') as FormArray).length > 0;

    const isNotCurrentCompany = targetCompanyId !== this.currentCompanyId;

    return hasCompany && hasUser && hasSelectedObjects && isNotCurrentCompany;
  }

  public getFormData() {
    const {
      feed_ids,
      dashboard_ids,
      dimension_ids,
      copy_only_selected,
      company_id,
      target_user_id,
    } = this.form.value;

    // * send [] for "feed_ids" and "dashboard_ids"
    // * if "selectAll" is true
    const feeds = copy_only_selected ? [...feed_ids, ...dimension_ids] : [];
    const dashboards = copy_only_selected ? dashboard_ids : [];

    return {
      source_workspace_id: this.objects.map((obj) => obj.id),
      target_company_id: company_id,
      target_user_id,
      copy_only_selected,
      feed_ids: feeds,
      dashboard_ids: dashboards,
    };
  }

  // ! helper method to emit data changes to parent popup
  private emitDataChange(type: string, data: any): void {
    this.componentDataSubject.next({
      type,
      data,
      timestamp: Date.now(),
    });
  }
}
